import { layersApi, type LayerFeatureCollection } from '../lib/layersApi';
import { mediaUrl } from '../lib/mediaApi';
import type { LayerConfig } from './demoLayers';

/** Above this, skip the eager "fetch every feature" call entirely — it's exactly what
 * would freeze the tab (large JSON parse + tens of thousands of OL features built before
 * the map ever paints). Must stay >= maps-service's _MAX_FEATURES_RESPONSE: below that
 * cap a single unscoped fetch already returns the whole layer anyway, so there's nothing
 * for viewport-scoped loading to save. Layers over the threshold start with an empty
 * FeatureCollection and get filled in by MapContext's lazy-layer effect instead. */
const LAZY_FEATURE_THRESHOLD = 5000;

const colorBySource: Record<string, string> = {
  file: '#2563eb',
  wms: '#06b6d4',
  wmts: '#06b6d4',
  wfs: '#16a34a',
  ogc_api: '#06b6d4',
  database: '#2563eb',
  api: '#7c3aed',
  raster: '#d97706',
  internal: '#94a3b8',
};

/** Fetches every real (backend-persisted) layer attached to this map, together with
 * its actual features, and returns them ready to hand to <MapProvider initialRealLayers>.
 * Layers with is_visible=false (toggled off in Данные и слои) are skipped entirely —
 * they still exist and are editable there, they just don't render on the map.
 * Best-effort: a single layer's features failing to load doesn't block the others. */
export async function loadRealLayerConfigs(mapId: string): Promise<LayerConfig[]> {
  const layerDtos = await layersApi.listLayers(mapId);

  const configs = await Promise.all(
    layerDtos
      .filter((dto) => dto.is_visible && dto.status !== 'draft')
      .map(async (dto): Promise<LayerConfig | null> => {
        try {
          if (dto.source === 'wms' && dto.wms) {
            return {
              id: dto.id,
              name: dto.name,
              color: dto.style_color ?? colorBySource[dto.source] ?? '#06b6d4',
              opacity: dto.style_opacity ?? 1,
              count: dto.object_count,
              defaultVisible: dto.status !== 'error',
              warning: dto.status === 'error' ? 'Ошибка загрузки' : undefined,
              wms: dto.wms,
              isReal: true,
              group: dto.group,
              geometryType: dto.geometry_type,
              status: dto.status,
              canEditFeatures: false,
            };
          }

          if (dto.source === 'raster' && dto.raster) {
            return {
              id: dto.id,
              name: dto.name,
              color: dto.style_color ?? colorBySource[dto.source] ?? '#d97706',
              opacity: dto.style_opacity ?? 1,
              count: dto.object_count,
              defaultVisible: dto.status !== 'error',
              warning: dto.status === 'error' ? 'Ошибка загрузки' : undefined,
              raster: dto.raster,
              isReal: true,
              group: dto.group,
              geometryType: dto.geometry_type,
              status: dto.status,
              canEditFeatures: false,
            };
          }

          if (dto.source === 'wmts' && dto.wmts) {
            return {
              id: dto.id,
              name: dto.name,
              color: dto.style_color ?? colorBySource[dto.source] ?? '#06b6d4',
              opacity: dto.style_opacity ?? 1,
              count: dto.object_count,
              defaultVisible: dto.status !== 'error',
              warning: dto.status === 'error' ? 'Ошибка загрузки' : undefined,
              wmts: dto.wmts,
              isReal: true,
              group: dto.group,
              geometryType: dto.geometry_type,
              status: dto.status,
              canEditFeatures: false,
            };
          }

          const lazy = dto.object_count > LAZY_FEATURE_THRESHOLD;
          const [featureCollection, detail] = await Promise.all([
            lazy
              ? Promise.resolve<LayerFeatureCollection>({ type: 'FeatureCollection', features: [] })
              : layersApi.getFeatures(dto.id),
            layersApi.getLayer(dto.id),
          ]);
          const truncatedWarning = featureCollection.truncated
            ? `Показаны первые ${featureCollection.features.length.toLocaleString('ru-RU')} из ${(featureCollection.total_count ?? dto.object_count).toLocaleString('ru-RU')} — слишком много объектов для карты`
            : undefined;
          return {
            id: dto.id,
            name: dto.name,
            color: dto.style_color ?? colorBySource[dto.source] ?? '#2563eb',
            opacity: dto.style_opacity ?? 1,
            count: dto.object_count,
            defaultVisible: dto.status !== 'error',
            warning: dto.status === 'error' ? 'Ошибка загрузки' : truncatedWarning,
            geojson: featureCollection as LayerConfig['geojson'],
            isReal: true,
            lazy,
            group: dto.group,
            geometryType: dto.geometry_type,
            status: dto.status,
            popupFieldCodes: detail.fields.filter((f) => f.visible_in_popup).map((f) => f.code),
            canEditFeatures: dto.can_edit_features,
            fields: detail.fields,
            photoByFeatureId: Object.fromEntries(
              featureCollection.features.map((f) => [f.id, f.photo_file_id ?? null]),
            ),
            markerMode: dto.style_marker_mode === 'icon' ? 'icon' : 'color',
            markerIconUrl: dto.style_marker_icon_file_id ? mediaUrl(dto.style_marker_icon_file_id) : undefined,
            styleType: detail.style_type,
            styleRules: detail.style_rules,
            styleFallback: detail.style_fallback,
          };
        } catch {
          return null;
        }
      }),
  );

  return configs.filter((c): c is LayerConfig => c !== null);
}
